import { getMessage } from '../../../../common/utils/MessageUtils'
import { mergeErrors } from '../extra/helpers'
import '../extra/types'

/*==================================
 定数定義
 ==================================*/

// 各チェック対象の必須項目と和名・フィールド名を定義
// employeeCountSetting のフィールド定義
export const employeeCountSettingFields = [
  {
    key: 'employeeOnLeaveTarget',
    label: '休職者の設定',
    fieldName: 'employeeOnLeaveTargetSetting',
    checkTypes: ['required']
  },
  {
    key: 'intraGroupSecondmentSource',
    label: '単独出向の設定（グループ内出向）',
    fieldName: 'individualSecondmentSetting',
    checkTypes: ['required']
  },
  {
    key: 'interGroupSecondmentSource',
    label: '単独出向の設定（グループ外出向）',
    fieldName: 'individualSecondmentSetting',
    checkTypes: ['required']
  },
  {
    key: 'interGroupSecondmentDestination',
    label: '単独出向の設定（グループ外出向）',
    fieldName: 'individualSecondmentSetting',
    checkTypes: ['required']
  },
  {
    key: 'concurrentSecondmentTarget',
    label: '兼務出向の設定',
    fieldName: 'concurrentSecondmentTargetSetting',
    checkTypes: ['required']
  }
]

// employmentTypeSetting のフィールド定義
export const employmentTypeSettingFields = [
  {
    key: 'securitiesEmployeeType',
    subKeys: ['employmentType'],
    label: '有報従業員区分',
    fieldName: 'employmentTypeSetting',
    checkTypes: ['required']
  },
  {
    key: 'fteEstimatedFactor',
    subKeys: ['employmentType'],
    label: 'FTE雇用区分概算係数',
    fieldName: 'employmentTypeSetting',
    checkTypes: ['required', 'numRange']
  },
  {
    key: 'fteActualWorkingHoursCalcMethod',
    subKeys: ['employmentType'],
    label: 'FTE実労働時間集計区分',
    fieldName: 'employmentTypeSetting',
    checkTypes: ['required']
  }
]

// prescribedWorkingHoursMonth のフィールド定義
export const prescribedWorkingHoursMonthFields = [
  {
    key: 'prescribedWorkingHoursPerDay',
    label: '所定労働時間／日',
    fieldName: 'prescribedWorkingHoursMonthSetting',
    checkTypes: ['required']
  },
  {
    key: 'workingDays',
    subKeys: ['month'],
    label: '営業日数',
    fieldName: 'prescribedWorkingHoursMonthSetting',
    checkTypes: ['required']
  }
]

// prescribedWorkingHoursYear のフィールド定義
export const prescribedWorkingHoursYearFields = [
  { key: 'weeksPerYear', label: '年間週数', fieldName: 'prescribedWorkingHoursYearSetting', checkTypes: ['required'] },
  {
    key: 'prescribedWorkingHoursPerWeek',
    label: '所定労働時間／週',
    fieldName: 'prescribedWorkingHoursYearSetting',
    checkTypes: ['required']
  }
]

/**
 * 必須チェックを行う汎用関数（重複メッセージ省く）
 *
 * @param {Array} items - チェック対象の配列
 * @param {Array} fieldDefs - フィールド定義配列
 * @returns {Object} { [fieldName]: { messages: string[], targets: string[] } }
 */
export function validateRequiredFields(items, fieldDefs) {
  const errors = {}

  if (!Array.isArray(items)) return errors

  items.forEach((item) => {
    fieldDefs
      .filter((field) => field.checkTypes.includes('required'))
      .forEach(({ key, subKeys, label, fieldName }) => {
        const value = item[key]
        if (value === undefined || value === null || value === '') {
          if (!errors[fieldName]) {
            errors[fieldName] = {
              messages: [],
              targets: []
            }
          }
          const message = getMessage('E010001', label)
          if (!errors[fieldName].messages.includes(message)) {
            errors[fieldName].messages.push(message)
          }

          // 複合キーを作成
          let compositeKey = key
          if (Array.isArray(subKeys) && subKeys.length > 0) {
            const subValues = subKeys.map((k) => item[k] ?? '').filter((v) => v !== '')
            if (subValues.length > 0) {
              compositeKey += '_' + subValues.join('_')
            }
          }
          if (!errors[fieldName].targets.includes(compositeKey)) {
            errors[fieldName].targets.push(compositeKey)
          }
        }
      })
  })

  return errors
}

/**
 * 数値範囲チェックを行う汎用関数（重複メッセージ省く）
 *
 * @param {Array} items - チェック対象の配列
 * @param {Array} fieldDefs - フィールド定義配列
 * @param {number} min - 最小値
 * @param {number} max - 最大値
 * @param {string} minOperator - 最小値との比較演算子（'<', '<=', '>', '>=') ※例：'>' の場合は `項目値 > 0` をチェック
 * @param {string} maxOperator - 最大値との比較演算子（'<', '<=', '>', '>=') ※例：'<=' の場合は `項目値 <= 1` をチェック）
 * @returns {Object} { [fieldName]: { messages: string[], targets: string[] } }
 */
export function validateNumberRangeFields(items, fieldDefs, min, max, minOperator = '>', maxOperator = '<=') {
  const errors = {}

  if (!Array.isArray(items)) return errors

  items.forEach((item) => {
    fieldDefs
      .filter((field) => field.checkTypes.includes('numRange'))
      .forEach(({ key, subKeys, label, fieldName }) => {
        // 指定されたキーのみを対象にチェック

        const value = item[key]
        const numValue = Number(value)

        let isMinInvalid = false
        let isMaxInvalid = false

        // 最小値チェック
        switch (minOperator) {
          case '>':
            isMinInvalid = numValue > min
            break
          case '>=':
            isMinInvalid = numValue >= min
            break
          case '<':
            isMinInvalid = numValue < min
            break
          case '<=':
            isMinInvalid = numValue <= min
            break
          default:
            isMinInvalid = false
        }

        // 最大値チェック
        switch (maxOperator) {
          case '>':
            isMaxInvalid = numValue > max
            break
          case '>=':
            isMaxInvalid = numValue >= max
            break
          case '<':
            isMaxInvalid = numValue < max
            break
          case '<=':
            isMaxInvalid = numValue <= max
            break
          default:
            isMaxInvalid = false
        }

        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          (isNaN(numValue) || !isMinInvalid || !isMaxInvalid)
        ) {
          if (!errors[fieldName]) {
            errors[fieldName] = { messages: [], targets: [] }
          }
          const message = getMessage('E010015', label, String(min), String(max))
          if (!errors[fieldName].messages.includes(message)) {
            errors[fieldName].messages.push(message)
          }

          // 複合キーを作成
          let compositeKey = key
          if (Array.isArray(subKeys) && subKeys.length > 0) {
            const subValues = subKeys.map((k) => item[k] ?? '').filter((v) => v !== '')
            if (subValues.length > 0) {
              compositeKey += '_' + subValues.join('_')
            }
          }
          if (!errors[fieldName].targets.includes(compositeKey)) {
            errors[fieldName].targets.push(compositeKey)
          }
        }
      })
  })
  return errors
}

/**
 * EmploymentTypeSetting の必須チェックと数値範囲チェックを統合する関数
 *
 * @param {Array} items - チェック対象の employmentTypeSetting 配列
 * @param {Array} fieldDefs - フィールド定義配列
 * @returns {Object} { [fieldName]: { messages: string[], targets: string[] } }
 */
export function validateEmploymentTypeSetting(items, fieldDefs) {
  // 必須チェックを実行
  const requiredErrors = validateRequiredFields(items, fieldDefs)

  // 数値範囲チェックを実行（FTE概算係数のみ）
  // 0 < x <= 1 の範囲をチェック
  const numberRangeErrors = validateNumberRangeFields(
    items,
    fieldDefs,
    0, // 最小値
    1, // 最大値
    '>', // 演算子（0 < x）
    '<=' // 演算子（x >= 1）
  )

  // 必須チェックと数値範囲チェックのエラーをマージして返す
  return mergeErrors(requiredErrors, numberRangeErrors)
}
